import { Button } from '@carboncopyco/ui';
import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { mixed, object } from 'yup';
import type { MonacoEditor } from '../CodeEditor';
import { CodeInput, schemaResolver } from '../TransactionRuleForm';

const fieldName = 'json';

const schema = object()
  .shape({
    [fieldName]: mixed(),
  })
  .json()
  .typeError('Invalid JSON');

export interface EditJsonFormProps {
  value: string;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}

export const EditJsonForm = ({
  value,
  onCancel,
  onSubmit,
}: EditJsonFormProps) => {
  const editorRef = useRef<MonacoEditor>();
  const methods = useForm({
    defaultValues: {
      [fieldName]: value,
    },
    resolver: schemaResolver(schema),
  });
  const [isLegalJson, setIsLegalJson] = useState<boolean>(true);
  const { register, handleSubmit, trigger, setValue, watch } = methods;
  const json = watch(fieldName);

  const formatJson = () =>
    editorRef.current
      ?.getAction('editor.action.formatDocument')
      ?.run()
      .then(() => editorRef.current?.revealLine(1));

  const submitForm = ({ json }) => onSubmit(json);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(submitForm)(e);
        }}
        className="w-[860px] flex flex-col h-full gap-4"
      >
        <div className="flex flex-1" />
        <input
          {...register(fieldName)}
          hidden
        />
        <CodeInput
          onMount={(editor) => {
            editorRef.current = editor;
            formatJson();
          }}
          onChange={(selectors) => {
            setValue(fieldName, selectors, {
              shouldDirty: true,
            });
            trigger(fieldName);
          }}
          onError={(errors) => {
            const errorMessages = errors.map(({ message }) => message);
            setIsLegalJson(!errorMessages.length);
          }}
          value={json}
          language="json"
          height={300}
          state={methods.formState.errors[fieldName] ? 'error' : 'normal'}
        />
        <div className="flex gap-2.5 items-center">
          <div className="flex-1" />
          <Button
            label="Cancel"
            link
            dark
            onClick={onCancel}
          />
          <Button
            label="Save"
            submit
            disabled={!isLegalJson}
          />
        </div>
      </form>
    </FormProvider>
  );
};
